import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '#root/lib/utils';
import styles from './timeline.module.scss';

/**
 * Variantes de timeline usando class-variance-authority + SCSS modules
 */
const timelineVariants = cva(styles.timeline, {
  variants: {
    variant: {
      dots: styles.dots,
      numbered: styles.numbered,
    },
  },
  defaultVariants: {
    variant: 'dots',
  },
});

export interface TimelineProps
  extends Omit<React.OlHTMLAttributes<HTMLOListElement>, 'children'>,
    VariantProps<typeof timelineVariants> {
  /**
   * Pasos en orden. El último se pinta relleno como resultado final.
   */
  items: React.ReactNode[];
}

/**
 * Timeline Component - Design System
 *
 * Pasos en orden unidos por una línea vertical.
 * - `dots` (default): un flujo que ocurre, con varios actores ("¿Cómo funciona…?").
 * - `numbered`: instrucciones que sigue el usuario ("¿Cómo hago…?").
 *
 * Cuándo usar cada variante y cuándo no usarlo: .claude/skills/design-system/SKILL.md
 *
 * @example
 * <Timeline items={['El viajero entrega tu pedido en MBE', 'OLVA entrega en tu dirección']} />
 * <Timeline variant="numbered" items={['Ingresa a tu pedido', 'Aplica el cupón al pagar']} />
 */
const Timeline = React.forwardRef<HTMLOListElement, TimelineProps>(
  ({ className, variant, items, ...props }, ref) => (
    // role="list": Safari quita la semántica de lista cuando lleva list-style: none
    <ol ref={ref} role="list" className={cn(timelineVariants({ variant }), className)} {...props}>
      {items.map((item, index) => (
        <li key={index} className={styles.item}>
          {/* En numbered, el número sale de un contador CSS */}
          <span className={styles.marker} />
          <span className={styles.content}>{item}</span>
        </li>
      ))}
    </ol>
  )
);

Timeline.displayName = 'Timeline';

export { Timeline, timelineVariants };
